import axios from "axios";

import {

  ANTHRO_AI_BASE_URL,

  ANTHRO_AI_COGNITO_AUTH_BODY,

  ANTHRO_AI_COGNITO_AUTH_HEADERS,

  ANTHRO_AI_COGNITO_URL,

  getAnthroAiIdToken,

  setAnthroAiIdToken,

} from "./config";



export type ChatApiMessage = {

  role: "user" | "assistant" | string;

  content: string;

  timestamp?: string;

  created_at?: string;

};



export type ChatApiSession = {

  session_id: string;

  status?: string;

  created_at?: string;

  last_activity_at?: string;

  title?: string;

  messages?: ChatApiMessage[];

};



export type ChatSessionListItem = {

  session_id: string;

  created_at?: string;

  last_activity_at?: string;

  title?: string;

};



export type ChatHistoryResponse = {

  user_id?: string;

  sessions?: ChatApiSession[];

};



/** Single-session history from GET /chat-history?session_id=… */

export type ChatSessionHistoryResponse = {

  session_id?: string;

  status?: string;

  messages?: ChatApiMessage[];

};



export type ChatPostRequest = {

  message: string;

  session_id?: string;

  allowed_org_ids: string;

};



export type ChatPostResponse = {

  reply?: string;

  session_id?: string;

  status?: string;

  user_id?: string;

  created_at?: string;

};



export async function authenticateAnthroAi(): Promise<string> {

  const { data } = await axios.post<{

    AuthenticationResult?: { IdToken?: string; ExpiresIn?: number };

  }>(ANTHRO_AI_COGNITO_URL, ANTHRO_AI_COGNITO_AUTH_BODY, {

    headers: ANTHRO_AI_COGNITO_AUTH_HEADERS,

  });



  const token = String(data?.AuthenticationResult?.IdToken || "").trim();

  if (!token) {

    throw new Error("Cognito authentication failed: IdToken not returned.");

  }



  setAnthroAiIdToken(token, data?.AuthenticationResult?.ExpiresIn);

  return token;

}



function aiClient() {

  const token = getAnthroAiIdToken();

  if (!token) {

    throw new Error(

      "Missing Anthro AI IdToken. Authenticate before calling the chat API.",

    );

  }

  return axios.create({

    baseURL: ANTHRO_AI_BASE_URL,

    timeout: 120000,

    headers: {

      Authorization: `Bearer ${token}`,

      "Content-Type": "application/json",

    },

  });

}



function isAxiosErrorLike(

  err: unknown,

): err is { response?: { data?: unknown }; message?: string } {

  return (

    !!err &&

    typeof err === "object" &&

    (err as { isAxiosError?: boolean }).isAxiosError === true

  );

}



function toChatError(err: unknown): Error {

  if (isAxiosErrorLike(err)) {

    const data = err.response?.data as

      | { error?: string; message?: string }

      | string

      | undefined;

    const fromBody =

      typeof data === "string"

        ? data

        : data?.error || data?.message || undefined;

    return new Error(fromBody || err.message || "Chat request failed");

  }

  if (err instanceof Error) return err;

  return new Error("Chat request failed");

}



export async function postChatMessage(

  body: ChatPostRequest,

): Promise<ChatPostResponse> {

  try {

    const payload: Record<string, string> = {

      message: body.message,

      allowed_org_ids: body.allowed_org_ids,

    };

    if (body.session_id) {

      payload.session_id = body.session_id;

    }



    const { data } = await aiClient().post<ChatPostResponse>("/chat", payload);

    return data ?? {};

  } catch (err) {

    throw toChatError(err);

  }

}



export async function getChatSessions(): Promise<ChatSessionListItem[]> {

  try {

    const { data } = await aiClient().get<{ sessions?: ChatSessionListItem[] }>(

      "/sessions",

    );

    return Array.isArray(data?.sessions) ? data.sessions : [];

  } catch (err) {

    throw toChatError(err);

  }

}



export async function getChatHistoryBySession(

  sessionId: string,

): Promise<ChatSessionHistoryResponse> {

  try {

    const { data } = await aiClient().get<ChatSessionHistoryResponse>(

      "/chat-history",

      {

        params: { session_id: sessionId },

      },

    );

    return data ?? {};

  } catch (err) {

    throw toChatError(err);

  }

}


